"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type SessionUser = {
  userId: number | string | null;
  name: string | null;
  mobileNumber: string | null;
  email: string | null;
  entityId: string | null;
  onboardingStatus: string | null;
  /** Compatibility fields read by existing console pages. */
  companyName?: string | null;
  role?: string;
  phoneE164?: string | null;
};

type AuthContextValue = {
  user: SessionUser | null;
  ready: boolean;
  /** True once /api/auth/me has given a DEFINITIVE answer (200). While false,
   *  the session state is unknown (server unreachable) and nobody may treat
   *  the user as signed out. */
  known: boolean;
  /** Resolves true when the server answered definitively (200). */
  refresh: () => Promise<boolean>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

// Instant sync between same-origin console tabs; cross-origin (console <-> the
// dost.org website) is handled by the focus/visibility/poll re-check, since both
// read the same .dost.org session cookie.
const AUTH_CHANNEL = "dost-auth";
function announceAuthChange() {
  try {
    const bc = new BroadcastChannel(AUTH_CHANNEL);
    bc.postMessage("changed");
    bc.close();
  } catch {
    /* unsupported */
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ready, setReady] = useState(false);
  const [known, setKnown] = useState(false);

  /** Re-check the session. Resolves true when the server gave a definitive
   *  200 answer, false on transient failure (session left untouched). */
  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch("/api/auth/me", { cache: "no-store" });
      // Non-2xx means a transient upstream failure (503 from our own /me
      // proxy, gateway errors, …) - keep the current session untouched and
      // let the next re-check (focus/poll) recover. A definitive "signed
      // out" is always a 200 with { user: null }.
      if (!res.ok) return false;
      const data = (await res.json()) as { user: SessionUser | null };
      const normalized =
        data.user ?
          {
            ...data.user,
            phoneE164: data.user.phoneE164 ?? data.user.mobileNumber ?? null,
            mobileNumber: data.user.mobileNumber ?? data.user.phoneE164 ?? null,
          }
        : null;
      setUser(normalized);
      setKnown(true);
      return true;
    } catch {
      /* network blip - keep the current session; the poll will recover */
      return false;
    }
  }, []);

  // First load: retry a couple of times with a short backoff before declaring
  // ready, so one blip at page-open never bounces a signed-in user to /login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const delay of [0, 800, 2000]) {
        if (delay) await new Promise((r) => setTimeout(r, delay));
        if (cancelled) return;
        const definitive = await refresh();
        if (cancelled) return;
        if (definitive) break;
      }
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep auth in sync without reloads: re-check on focus/visibility, poll
  // periodically, and listen for same-origin tabs. Signing out on the website
  // clears the shared cookie, so the next re-check here logs the console out too.
  useEffect(() => {
    const recheck = () => void refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") recheck();
    };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", onVisible);
    const poll = window.setInterval(recheck, 30_000);

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(AUTH_CHANNEL);
      bc.onmessage = (e) => {
        if (e.data === "changed") recheck();
      };
    } catch {
      /* unsupported */
    }

    return () => {
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(poll);
      bc?.close();
    };
  }, [refresh]);

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST" }).catch(() => {});
    setUser(null);
    setKnown(true); // definitive: the user chose to sign out
    announceAuthChange();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, ready, known, refresh, logout }),
    [user, ready, known, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
